import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  SimpleChanges,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { EntityService } from '@core/services/entity/entity.service';
import { TranslateService } from '@ngx-translate/core';
import { MaxLength } from '@shared/constants/max-length-values';
import { Security } from '@shared/models/security';
import { HeaderService } from '@shared/services';
import { combineLatest, take } from 'rxjs';
import {
  GeneralRatesRuleEngine,
  SecurityType,
} from 'src/app/modules/admin/models';
import { InvestmentJurisdictionShort } from 'src/app/modules/admin/models/investment-jurisdiction-short';
import {
  InvestmentJurisdictionService,
  SecurityTypeService,
} from 'src/app/modules/admin/services';
import { TaxResultApprovalService } from 'src/app/modules/tax-result/services/tax-result-approval.service';
import {
  CreateSecurityRequest,
  CreateSecurityTaxRateRequest,
  ReevaluateSecurityRequest,
  SecurityResponseAuditable,
  UpdateSecurityRequest,
  UpdateSecurityTaxRateRequest,
} from 'src/app/web-api-client';
import { SecurityForm } from '../../abstractions/security-form';
import { Entity } from '../../models/entity';
import { UpsertSecurityState } from '../../models/upsert-security-state';
import { SecurityFormGroupDefinition } from '../../services/security-form-group-definition';
import { SecurityService } from '../../services/security.service';
import { RulesEngineViewComponent } from '../rules-engine-view/rules-engine-view.component';

@Component({
  standalone: false,
  selector: 'gstar-security-form',
  templateUrl: './security-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecurityFormComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private readonly generalRatesAllowedSecurityTypeNames: string[] = [
    'Corporate Debt',
    'Government Debt',
    'Listed Equity',
    'Unlisted Equity',
  ];

  private readonly router = inject(Router);
  private readonly securityService = inject(SecurityService);
  private readonly headerService = inject(HeaderService);
  private readonly securityFormGroupDefinition = inject(
    SecurityFormGroupDefinition
  );
  private readonly entityService = inject(EntityService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  private readonly securityTypeService = inject(SecurityTypeService);
  private readonly taxResultApprovalService = inject(TaxResultApprovalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() upsertSecurityState!: UpsertSecurityState;
  @Input() generalRateRule: GeneralRatesRuleEngine | null = null;
  @Input() generalRateRules: GeneralRatesRuleEngine[] = [];
  @Input() generalRateEntityTypeId: number | null = null;
  @Input() generalRateEntityTypeName: string | null = null;
  @Input()
  generalRateInvestmentJurisdiction: InvestmentJurisdictionShort | null = null;
  @Input() generalRateSecurityType: SecurityType | null = null;

  @Output() updatedSecurity: EventEmitter<Security> = new EventEmitter();
  @Output() addSecurity: EventEmitter<SecurityResponseAuditable> =
    new EventEmitter();

  @ViewChild('rulesEngineForm', { static: true })
  rulesEngineForm!: RulesEngineViewComponent;

  @ViewChild('modal', { static: false }) modal: ModalComponent | undefined;

  securityRequest: WritableSignal<
    CreateSecurityRequest | UpdateSecurityRequest | null
  > = signal(null);
  formValue: WritableSignal<SecurityForm | null> = signal(null);
  warningModalMessageKey: WritableSignal<string> = signal(
    'upsertSecurity.selectClientWarningMessage'
  );

  securityNameMaxLength: number = MaxLength.SecurityName;

  isSecurityEnabled: Signal<boolean> = computed(
    () => !!this.securityRequest() && this.securityFormGroupDefinition.valid
  );

  isUpsertFormSecurityEnabled: Signal<boolean> = computed(
    () => !!this.headerService.selectedClient()
  );

  hasNoQuestions: Signal<boolean> = computed(
    () => this.securityRequest()?.questionAnswers?.length === 0
  );
  routeBack!: string;
  private hasShownEqualJurisdictionsModal: boolean = false;

  ngOnDestroy(): void {
    this.formDefinition.reset();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.upsertSecurityState?.isGeneralRatesTransaction &&
      ('generalRateEntityTypeId' in changes ||
        'generalRateEntityTypeName' in changes ||
        'generalRateInvestmentJurisdiction' in changes ||
        'generalRateSecurityType' in changes ||
        'generalRateRules' in changes)
    ) {
      this.applyGeneralRateSelectionDefaults();
    }

    if ('generalRateRules' in changes && this.generalRateRules.length > 0) {
      this.applyGeneralRateRuleDefaults(this.generalRateRules[0]);
    }

    if ('generalRateRule' in changes && this.generalRateRule) {
      this.applyGeneralRateRuleDefaults(this.generalRateRule);
    }
  }

  get isFormNotEmpty(): boolean {
    return this.rulesEngineForm?.isFormNotEmpty();
  }

  get formDefinition(): SecurityFormGroupDefinition {
    return this.securityFormGroupDefinition;
  }

  get isContinueEnabled(): boolean {
    return (
      this.securityFormGroupDefinition.valid &&
      this.securityFormGroupDefinition.securityForm.dirty &&
      this.securityFormGroupDefinition.securityForm.touched &&
      this.rulesEngineForm.inProgress
    );
  }

  get areJurisdictionsEqual(): boolean {
    const residencyJurisdictionName =
      this.securityFormGroupDefinition.residencyJurisdictionNameValue
        ?.trim()
        .toLowerCase() ?? '';
    const investmentJurisdictionName =
      this.securityFormGroupDefinition.investmentJurisdictionValue?.name
        ?.trim()
        .toLowerCase() ?? '';

    return (
      residencyJurisdictionName.length > 0 &&
      investmentJurisdictionName.length > 0 &&
      residencyJurisdictionName === investmentJurisdictionName
    );
  }

  get allowedSecurityTypeNames(): string[] {
    return this.upsertSecurityState.isGeneralRatesTransaction
      ? this.generalRatesAllowedSecurityTypeNames
      : [];
  }

  ngOnInit(): void {
    this.headerService.selectedClient$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(client => {
        if (this.upsertSecurityState.securityId) {
          return;
        }

        this.securityFormGroupDefinition.reset();
        if (!client) {
          this.openWarningModal(
            'upsertSecurity.selectClientWarningTitle',
            'upsertSecurity.selectClientWarningMessage'
          );
        }
      });

    this.updateAcquisitionDateControl();

    if (this.upsertSecurityState.securityId) {
      this.securityFormGroupDefinition.loadSecurityInfo(
        this.upsertSecurityState.securityId
      );
    }
    this.routeBack = !this.taxResultApprovalService.isEditingSecuritiesRules()
      ? '/portfolio'
      : 'tax-results/edit';
  }

  ngAfterViewInit(): void {
    if (
      !this.isUpsertFormSecurityEnabled() &&
      !this.upsertSecurityState.securityId
    ) {
      this.openWarningModal(
        'upsertSecurity.selectClientWarningTitle',
        'upsertSecurity.selectClientWarningMessage'
      );
    }
  }

  cancel(): void {
    this.reset();
    this.router.navigate([this.routeBack]);
  }

  reset(): void {
    this.rulesEngineForm.resetSchema();
    this.formValue.set(null);
    this.securityRequest.set(null);
  }

  continue(): void {
    if (
      this.securityFormGroupDefinition.securityForm.valid &&
      !this.areJurisdictionsEqual
    ) {
      this.formDefinition.clearRuleForm();
      this.formValue.set(this.getSecurityFormValue());
    }
  }

  createSecurity(): void {
    if (this.securityRequest() !== null) {
      this.securityService
        .createSecurity(this.securityRequest() as CreateSecurityRequest)
        .subscribe((securityResponseAudit: SecurityResponseAuditable) => {
          this.securityFormGroupDefinition.clearRuleForm();
          this.addSecurity.emit(securityResponseAudit);
        });
    }
  }

  updateSecurity(): void {
    this.securityService
      .updateSecurity(
        this.upsertSecurityState.securityId,
        this.getUpdateRequest()
      )
      .subscribe(() => {
        this.securityFormGroupDefinition.clearRuleForm();
        this.updatedSecurity.emit();
      });
  }

  selectInvestmentJurisdiction(
    investmentJurisdiction: InvestmentJurisdictionShort | null | undefined
  ): void {
    this.securityFormGroupDefinition.investmentJurisdiction.setValue(
      investmentJurisdiction
    );
    this.securityFormGroupDefinition.investmentJurisdiction.markAsDirty();
    this.securityFormGroupDefinition.investmentJurisdiction.markAsTouched();
    this.showEqualJurisdictionsAlertIfNeeded();
  }

  selectSecurityType(securityType: SecurityType | null | undefined): void {
    this.securityFormGroupDefinition.securityType.setValue(securityType);
    this.securityFormGroupDefinition.securityType.markAsDirty();
    this.securityFormGroupDefinition.securityType.markAsTouched();
  }

  updateEntity(entity: Entity | null | undefined): void {
    this.securityFormGroupDefinition.entity.setValue(entity);
    this.securityFormGroupDefinition.entity.markAsDirty();
    this.securityFormGroupDefinition.entity.markAsTouched();
    this.prepopulateEntityRelatedFields(entity);
  }

  setFormCompleted(
    request: CreateSecurityRequest | UpdateSecurityRequest
  ): void {
    this.applyGeneralRateRuleTaxRates(request);
    this.securityRequest.set(request);
    if (this.hasNoQuestions()) {
      if (!this.upsertSecurityState.securityId) {
        this.createSecurity();
      } else {
        this.updateSecurity();
      }
    }
  }

  private prepopulateEntityRelatedFields(
    entity: Entity | null | undefined
  ): void {
    if (!entity) {
      this.securityFormGroupDefinition.entity.reset();
      this.securityFormGroupDefinition.entityTypeName.reset();
      this.securityFormGroupDefinition.residencyJurisdictionName.reset();
    } else if (entity?.entityId) {
      this.entityService
        .getEntityById(entity?.entityId)
        .subscribe((entity: Entity) => {
          this.securityFormGroupDefinition.residencyJurisdictionName.setValue(
            entity.residencyJurisdiction?.name ?? ''
          );
          this.securityFormGroupDefinition.entityTypeName.setValue(
            entity.entityType?.name ?? ''
          );
          this.showEqualJurisdictionsAlertIfNeeded();
          this.cdr.markForCheck();
        });
    }
  }

  private showEqualJurisdictionsAlertIfNeeded(): void {
    if (this.areJurisdictionsEqual && !this.hasShownEqualJurisdictionsModal) {
      this.openWarningModal(
        'upsertSecurity.equalJurisdictionsWarningTitle',
        'upsertSecurity.equalJurisdictionsWarningMessage'
      );
      this.hasShownEqualJurisdictionsModal = true;
      return;
    }

    if (!this.areJurisdictionsEqual) {
      this.hasShownEqualJurisdictionsModal = false;
    }
  }

  private openWarningModal(titleKey: string, messageKey: string): void {
    this.warningModalMessageKey.set(messageKey);
    if (this.modal) {
      this.modal.title = this.translate.instant(titleKey);
      this.modal.showModal('modal-warning');
    }
  }

  private getUpdateRequest(): ReevaluateSecurityRequest {
    return this.securityRequest() as ReevaluateSecurityRequest;
  }

  private getSecurityFormValue(): SecurityForm {
    const formValue = this.securityFormGroupDefinition.securityForm
      .value as SecurityForm;

    if (!this.upsertSecurityState.isGeneralRatesTransaction) {
      return formValue;
    }

    return {
      ...formValue,
      clientId: this.headerService.selectedClient()?.id,
      entity: null,
      generalRateEntityTypeId:
        formValue.generalRateEntityTypeId ??
        this.effectiveGeneralRateRules[0]?.entityTypeId,
    };
  }

  private updateAcquisitionDateControl(): void {
    if (this.upsertSecurityState.isSpecificAdviceTransaction) {
      this.securityFormGroupDefinition.acquisitionDate.addValidators(
        Validators.required
      );
    }
    if (this.upsertSecurityState.isGeneralRatesTransaction) {
      this.securityFormGroupDefinition.entity.clearValidators();
      this.securityFormGroupDefinition.entity.setValue(null);
      this.securityFormGroupDefinition.acquisitionDate.setValue(undefined);
    }

    this.securityFormGroupDefinition.entity.updateValueAndValidity();
    this.securityFormGroupDefinition.acquisitionDate.updateValueAndValidity();
  }

  private applyGeneralRateRuleTaxRates(
    request: CreateSecurityRequest | UpdateSecurityRequest
  ): void {
    if (
      !this.upsertSecurityState.isGeneralRatesTransaction ||
      this.effectiveGeneralRateRules.length === 0
    ) {
      return;
    }

    for (const rule of this.effectiveGeneralRateRules) {
      this.applyGeneralRateRuleTaxRatesForRule(request, rule);
    }
  }

  private applyGeneralRateRuleTaxRatesForRule(
    request: CreateSecurityRequest | UpdateSecurityRequest,
    rule: GeneralRatesRuleEngine
  ): void {
    const incomeTypeId = rule.incomeTypeId;
    if (!incomeTypeId) {
      return;
    }

    const templateRates = this.getGeneralRateRuleRates(rule);
    if (templateRates.length === 0) {
      return;
    }

    const explanation = rule.explanation ?? '';
    const currentTaxRates = request.taxRates ?? [];
    const matchingTaxRates = currentTaxRates.filter(
      item => item.taxResultItemTypeId === incomeTypeId
    );

    if (matchingTaxRates.length === 0) {
      request.taxRates = [
        ...currentTaxRates,
        ...this.createGeneralRateRuleTaxRates(
          request,
          incomeTypeId,
          explanation,
          templateRates
        ),
      ] as typeof request.taxRates;
      return;
    }

    const remainingTaxRates = currentTaxRates.filter(
      item => item.taxResultItemTypeId !== incomeTypeId
    );

    // Keep existing computed tax metadata and only replace rate/description.
    const updatedMatchingTaxRates =
      templateRates.length > 0
        ? templateRates.map((rateValue, index) => {
            const source = matchingTaxRates[index] ?? matchingTaxRates[0];

            return this.cloneTaxRateWithTemplate(
              source,
              explanation,
              rateValue
            );
          })
        : matchingTaxRates.map(source =>
            this.cloneTaxRateWithTemplate(source, explanation)
          );

    request.taxRates = [
      ...remainingTaxRates,
      ...updatedMatchingTaxRates,
    ] as typeof request.taxRates;
  }

  private get effectiveGeneralRateRules(): GeneralRatesRuleEngine[] {
    return this.generalRateRules.length > 0
      ? this.generalRateRules
      : this.generalRateRule
        ? [this.generalRateRule]
        : [];
  }

  private getGeneralRateRuleRates(rule: GeneralRatesRuleEngine): number[] {
    const rates = (rule.rates ?? []).filter(
      (rate): rate is number => rate !== undefined && rate !== null
    );

    if (rates.length > 0) {
      return rates;
    }

    return rule.rate !== undefined && rule.rate !== null ? [rule.rate] : [];
  }

  private createGeneralRateRuleTaxRates(
    request: CreateSecurityRequest | UpdateSecurityRequest,
    incomeTypeId: number,
    description: string,
    rates: number[]
  ): Array<CreateSecurityTaxRateRequest | UpdateSecurityTaxRateRequest> {
    if (request instanceof UpdateSecurityRequest) {
      return rates.map(
        rate =>
          new UpdateSecurityTaxRateRequest({
            taxResultItemTypeId: incomeTypeId,
            rate,
            description,
            isActive: true,
          })
      );
    }

    return rates.map(
      rate =>
        new CreateSecurityTaxRateRequest({
          taxResultItemTypeId: incomeTypeId,
          rate,
          description,
          isActive: true,
        })
    );
  }

  private cloneTaxRateWithTemplate(
    source: CreateSecurityTaxRateRequest | UpdateSecurityTaxRateRequest,
    description: string,
    rate?: number
  ): CreateSecurityTaxRateRequest | UpdateSecurityTaxRateRequest {
    const payload = {
      ...source,
      description,
      rate: rate ?? source.rate,
      isActive: true,
    };

    return source instanceof UpdateSecurityTaxRateRequest
      ? new UpdateSecurityTaxRateRequest(payload)
      : new CreateSecurityTaxRateRequest(payload);
  }

  /**
   * Applies the default values from the given general rate rule to the form controls.
   * @param rule General rate rule.
   */
  private applyGeneralRateRuleDefaults(rule: GeneralRatesRuleEngine): void {
    const securityTypeId =
      this.generalRateSecurityType?.securityTypeId ?? rule.securityTypeIds?.[0];
    const investmentJurisdictionId =
      this.generalRateInvestmentJurisdiction?.investmentJurisdictionId ??
      rule.investmentJurisdictionIds?.[0];

    this.formDefinition.securityForm.patchValue({
      generalRateEntityTypeId:
        this.generalRateEntityTypeId ?? rule.entityTypeId,
      entity: null,
    });
    this.formDefinition.entityTypeName.setValue(
      this.generalRateEntityTypeName ?? rule.entityTypeName ?? ''
    );
    this.formDefinition.residencyJurisdictionName.setValue('');

    combineLatest([
      this.securityTypeService.activeSecurityTypeList(),
      this.investmentJurisdictionService.activeInvestmentJurisdictionList(),
    ])
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(([securityTypesResponse, investmentJurisdictionsResponse]) => {
        const matchedSecurityType = securityTypesResponse.items.find(
          (item: SecurityType) => item.securityTypeId === securityTypeId
        );
        const matchedInvestmentJurisdiction =
          investmentJurisdictionsResponse.items.find(
            (item: InvestmentJurisdictionShort) =>
              item.investmentJurisdictionId === investmentJurisdictionId
          );

        this.formDefinition.securityType.setValue(matchedSecurityType);
        this.formDefinition.investmentJurisdiction.setValue(
          matchedInvestmentJurisdiction
        );

        this.formDefinition.securityType.markAsTouched();
        this.formDefinition.investmentJurisdiction.markAsTouched();
        this.cdr.markForCheck();
      });
  }

  private applyGeneralRateSelectionDefaults(): void {
    this.formDefinition.securityForm.patchValue({
      entity: null,
      generalRateEntityTypeId: this.generalRateEntityTypeId,
      investmentJurisdiction: this.generalRateInvestmentJurisdiction,
      securityType: this.generalRateSecurityType,
    });
    this.formDefinition.entityTypeName.setValue(
      this.generalRateEntityTypeName ?? ''
    );
    this.formDefinition.residencyJurisdictionName.setValue('');
    this.formDefinition.investmentJurisdiction.markAsTouched();
    this.formDefinition.securityType.markAsTouched();
  }
}
